import { useEffect, useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { isEqual } from "lodash";
import ClientTableContainer from "../components/ClientTableContainer";
import ClientSearchForm from "../components/ClientSearchForm";
import useClientList from "../hooks/useClientList";
import { Client, ClientUpdatePayload } from "../types/client";
import ClientFormModal from "../components/ClientFormModal";

/**
 * ClientList component renders the client management interface.
 * It includes a header, a title section, a search form, and a table container.
 * @example
 * return (<ClientList />)
 * @returns {Element} The rendered component.
 */
function ClientList() {
  const navigate = useNavigate();
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [isShowModal, setIsShowModal] = useState(false);

  const {
    clients,
    hasMore,
    setQuery,
    setTempQuery,
    tempQuery,
    query,
    handleSubmitUpdate,
    isFetching,
    totalRecord,
  } = useClientList();

  const handleCloseModal = () => {
    setIsShowModal(false);
    setSelectedClient(null);
  };

  const handleSelectedClient = (client: Client) => {
    setSelectedClient(client);
    setIsShowModal(true);
  };

  const handleSubmitUpdateClient = async (form: ClientUpdatePayload) => {
    const requestPayload: ClientUpdatePayload = {
      client_cd: selectedClient?.client_cd || "",
      mst_client: {
        client_name: form.client_name,
        // shipping_to_a: selectedClient?.shipping_to_a,
        // shipping_to_b: selectedClient?.shipping_to_b,
        // shipping_to_c: selectedClient?.shipping_to_c,
      },
      status_flag: form.status_flag,
    };
    await handleSubmitUpdate(requestPayload);
    setIsShowModal(false);
  };

  const setPage = () => {
    setQuery((prevQuery) => ({ ...prevQuery, offset: prevQuery.offset + 1 }));
  };

  const handleSubmitSearch = () => {
    if (
      isEqual(tempQuery, {
        client_cd_or_name: query.client_cd_or_name,
        pref_cd: query.pref_cd,
        status_flag: query.status_flag,
      })
    )
      return;
    setQuery((prevQuery) => ({ ...prevQuery, ...tempQuery, offset: 1 }));
  };

  useEffect(() => {
    const handleScroll = () => {
      if (
        Math.round(window.innerHeight + window.scrollY) >= document.body.offsetHeight &&
        hasMore > 0 &&
        !isFetching
      ) {
        setPage();
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [hasMore, isFetching]);

  return (
    <>
      <Box sx={{ px: { xs: 2, md: 2.5, lg: 5 }, pt: { xs: 2, md: 2.5, lg: 3 } }}>
        <Button onClick={() => navigate(-1)} variant="custom-outlined">
          <Box>＜ 戻る</Box>
        </Button>
      </Box>
      <Box
        sx={{
          p: { xs: 2, md: 2.5, lg: 5 },
          gap: 2,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box>
          <Typography
            fontWeight="medium"
            color="var(--gray-700)"
            fontSize={{ xs: 24, md: 28, lg: 23 }}
          >
            マスタ管理
          </Typography>
          <Typography
            marginLeft={4}
            fontWeight="medium"
            color="var(--gray-700)"
            fontSize={{ xs: 20, md: 28 }}
          >
            得意先一覧
          </Typography>
        </Box>
        <ClientSearchForm setTempQuery={setTempQuery} onSubmit={handleSubmitSearch} tempQuery={tempQuery} />
      </Box>

      <ClientTableContainer
        clients={clients}
        selectClient={handleSelectedClient}
        totalRecord={totalRecord}
      />

      {selectedClient && (
        <ClientFormModal
          clientDetail={selectedClient}
          visible={isShowModal}
          onClose={handleCloseModal}
          onHandleSubmit={handleSubmitUpdateClient}
          isLoading={isFetching}
        />
      )}
    </>
  );
}

export default ClientList;
